import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, FileText } from "lucide-react";
import {
  formatMoney,
  SALES_DOCUMENT_STATUS_LABELS,
  SALES_DOCUMENT_STATUS_TONE,
} from "@finora/config";
import { SalesDocumentStatus } from "@finora/types";
import { Button, StatusBadge, EmptyState } from "@finora/ui";
import { api } from "../../lib/api";

export function PublicQuotePage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();

  const { data: quote, isLoading, isError } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => api.getPublicQuote(token!),
    enabled: !!token,
  });

  const accept = useMutation({
    mutationFn: () => api.acceptPublicQuote(token!),
    onSuccess: (updated) => queryClient.setQueryData(["public-quote", token], updated),
  });
  const reject = useMutation({
    mutationFn: () => api.rejectPublicQuote(token!),
    onSuccess: (updated) => queryClient.setQueryData(["public-quote", token], updated),
  });

  return (
    <div className="min-h-screen bg-app-bg py-10">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-6 flex items-center gap-2">
          <img src="/brand-logo.png" alt="HAFiskal" className="h-6 w-auto" />
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-500">Učitavanje...</p>
        ) : isError || !quote ? (
          <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
            <EmptyState
              icon={FileText}
              title="Ponuda nije pronađena"
              description="Link je možda netačan ili je ponuda uklonjena."
            />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-surface p-6 shadow-subtle">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h1 className="text-lg font-semibold text-slate-900">
                    Ponuda {quote.documentNumber ?? ""}
                  </h1>
                  <p className="text-sm text-slate-500">
                    {quote.customer?.name ?? "Poštovani kupče"}
                  </p>
                </div>
                <StatusBadge
                  label={SALES_DOCUMENT_STATUS_LABELS[quote.status]}
                  tone={SALES_DOCUMENT_STATUS_TONE[quote.status]}
                />
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Količina</th>
                    <th>Cena</th>
                    {quote.items.some((i) => Number(i.discountAmount) > 0) && <th>Popust</th>}
                    <th>Ukupno</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.items.map((item) => (
                    <tr key={item.id}>
                      <td className="font-medium text-slate-800">{item.name}</td>
                      <td>
                        {item.quantity} {item.unit}
                      </td>
                      <td>{formatMoney(item.unitPriceGross)}</td>
                      {quote.items.some((i) => Number(i.discountAmount) > 0) && (
                        <td>{Number(item.discountAmount) > 0 ? formatMoney(item.discountAmount) : "—"}</td>
                      )}
                      <td className="font-medium">{formatMoney(item.grossAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between text-slate-500">
                    <span>Osnovica</span>
                    <span>{formatMoney(quote.netAmount)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>PDV</span>
                    <span>{formatMoney(quote.taxAmount)}</span>
                  </div>
                  {Number(quote.discountAmount) > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>Popust</span>
                      <span>-{formatMoney(quote.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-border pt-1 text-base font-semibold text-slate-900">
                    <span>Ukupno</span>
                    <span>{formatMoney(quote.grossAmount)}</span>
                  </div>
                </div>
              </div>

              {quote.customerNote && (
                <div className="mt-5 border-t border-border pt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                    Napomena
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{quote.customerNote}</p>
                </div>
              )}
            </div>

            {(
              [SalesDocumentStatus.SENT, SalesDocumentStatus.VIEWED] as SalesDocumentStatus[]
            ).includes(quote.status) ? (
              <div className="space-y-3">
                {quote.quoteAcceptanceText && (
                  <div className="rounded-xl border border-border bg-surface p-4 text-sm text-slate-600">
                    {quote.quoteAcceptanceText}
                  </div>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="success"
                    className="flex-1"
                    onClick={() => accept.mutate()}
                    disabled={accept.isPending || reject.isPending}
                  >
                    <CheckCircle2 className="h-4 w-4" aria-hidden /> Prihvati ponudu
                  </Button>
                  <Button
                    variant="danger"
                    className="flex-1"
                    onClick={() => reject.mutate()}
                    disabled={accept.isPending || reject.isPending}
                  >
                    <XCircle className="h-4 w-4" aria-hidden /> Odbij ponudu
                  </Button>
                </div>
              </div>
            ) : quote.status === SalesDocumentStatus.ACCEPTED ? (
              <div className="space-y-3">
                <p className="rounded-lg bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700">
                  Ponudu ste prihvatili. Hvala Vam!
                </p>
                <Button variant="secondary" className="w-full" onClick={() => (window.location.href = "/")}>
                  Nazad na početnu
                </Button>
              </div>
            ) : quote.status === SalesDocumentStatus.REJECTED ? (
              <div className="space-y-3">
                <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                  Ponudu ste odbili.
                </p>
                <Button variant="secondary" className="w-full" onClick={() => (window.location.href = "/")}>
                  Nazad na početnu
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
